import dayjs from "dayjs";
import { GetAppointment, GetSlot } from "@/services/fhir";
import { patientsByIdFrom, toBookedRow } from "../../provider/transformers/bookedRowTransformers";
import type { BookedTeleconsultoriaRow } from "../../provider/types";
import type { AgendaSlotRow, TeleconsultoriaAgendaSummary } from "../types";

interface DateRange {
  startDate: string;
  endDate: string;
}

/** Horários da agenda no período (mesma busca do AppointmentAccordion). */
export async function listAgendaSlots(
  summary: TeleconsultoriaAgendaSummary,
  range: DateRange,
): Promise<AgendaSlotRow[]> {
  const bundle: fhir5.Bundle | undefined = await GetSlot({
    schedule: summary.id,
    status: ["free", "busy-unavailable"],
    sort: "start",
    startDate: range.startDate,
    endDate: range.endDate,
  });
  return (bundle?.entry || [])
    .map((entry) => entry.resource)
    .filter((resource): resource is fhir5.Slot => resource?.resourceType === "Slot")
    // Horário que já terminou não serve mais para marcar.
    .filter((slot) => Boolean(slot.id && slot.start && slot.end && dayjs(slot.end).isAfter(dayjs())))
    .map((slot) => ({
      slotId: slot.id as string,
      start: slot.start as string,
      end: slot.end,
      status: slot.status,
    }));
}

/**
 * Consultas da agenda no período (mesma busca do AppointmentAccordion, com
 * `actor` = profissional da agenda; sem ele o scheduling responde 409).
 * upcoming: ainda vão acontecer. done: realizadas ou já passadas.
 */
export async function listAgendaAppointments(params: {
  summary: TeleconsultoriaAgendaSummary;
  range: DateRange;
  kind: "upcoming" | "done";
}): Promise<BookedTeleconsultoriaRow[]> {
  const { summary, range, kind } = params;
  const practitionerReference = summary.practitioner?.reference;
  if (!practitionerReference) return [];
  const bundle: fhir5.Bundle | undefined = await GetAppointment({
    schedule: summary.id,
    startDate: range.startDate,
    endDate: range.endDate,
    status: kind === "upcoming" ? ["booked", "pending", "proposed"] : ["booked", "fulfilled"],
    includePatient: true,
    scheduling: true,
    actor: practitionerReference,
  });
  const resources = (bundle?.entry || []).map((entry) => entry.resource);
  const patientsById = patientsByIdFrom(resources);
  const now = dayjs();
  return resources
    .filter((resource): resource is fhir5.Appointment => resource?.resourceType === "Appointment")
    .filter((appointment) =>
      kind === "upcoming"
        ? dayjs(appointment.end).isAfter(now)
        : appointment.status === "fulfilled" || dayjs(appointment.end).isBefore(now),
    )
    .map((appointment) =>
      toBookedRow(appointment, { patientsById, ownerReferences: new Set([practitionerReference]) }),
    )
    .filter((row): row is BookedTeleconsultoriaRow => Boolean(row))
    .sort((first, second) =>
      kind === "upcoming"
        ? dayjs(first.start).valueOf() - dayjs(second.start).valueOf()
        : dayjs(second.start).valueOf() - dayjs(first.start).valueOf(),
    );
}
